<template>
  <div class="member-select">
    <!-- 搜索区域 -->
    <div class="search-input">
      <el-input
        v-model="searchText"
        placeholder="搜索成员"
        prefix-icon="el-icon-search"
        clearable
        @clear="clearSearch"
        size="small"
      />
    </div>
    <div class="member-list" @scroll="handleScroll">
      <template v-if="!searchText">
        <!-- 所有人选项 -->
        <div
          class="member-item all-members"
          :class="{ selected: isSelected('all') }"
          @click="selectMember('all')"
        >
          <div class="member-icon-wrapper">
            <img
              class="member-icon iconfont icon-users"
              src="../../../assets//images/live-chat/all-icon.png"
              alt=""
            />
          </div>

          <span class="member-name">所有人</span>
          <i v-if="isSelected('all')" class="el-icon-check check-icon"></i>
        </div>

        <!-- 已选成员（如果有且不是所有人） -->
        <div
          v-if="selectedMember && selectedMember !== 'all'"
          class="member-item selected"
          @click="selectMember(selectedMember)"
        >
          <el-avatar :src="selectedMember.avatar" class="member-avatar">
          </el-avatar>
          <div class="member-info">
            <span class="member-name">
              <span
                class="role-tag"
                :class="getRoleClass(selectedMember.user_role)"
                >{{ getRoleLabel(selectedMember.user_role) }}</span
              >
              <span class="member-name-text">{{
                getMemberName(selectedMember)
              }}</span>
            </span>
          </div>
          <i class="el-icon-check check-icon"></i>
        </div>
      </template>

      <!-- 成员列表 -->
      <div
        v-for="member in displayMembers"
        :key="member === 'all' ? 'all' : member.user_id || member.id"
        class="member-item"
        :class="{ selected: isSelected(member) }"
        @click="selectMember(member)"
      >
        <el-avatar :src="member.avatar" class="member-avatar"> </el-avatar>
        <div class="member-info">
          <span class="member-name"
            ><span class="role-tag" :class="getRoleClass(member.user_role)">
              {{ getRoleLabel(member.user_role) }}</span
            >
            <span class="member-name-text">
              {{ getMemberName(member) }}
            </span>
          </span>
        </div>
        <i v-if="isSelected(member)" class="el-icon-check check-icon"></i>
      </div>

      <!-- 加载更多提示 -->
      <div v-if="loading" class="loading-more">
        <i class="el-icon-loading"></i>
        <span>加载中...</span>
      </div>

      <!-- 搜索无结果提示 -->
      <div
        v-if="searchText && members.length === 0 && !loading"
        class="no-result"
      >
        <span>未找到相关同学</span>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { getRoster } from "@saas/server-api/src/live-sdk";

export default {
  name: "MemberSelect",
  props: {
    showMemberSelect: {
      type: Boolean,
      default: false,
    },
    initialSelectedMember: {
      type: [Object, String],
      default: "all",
    },
    userInfo: {
      type: Object,
      default: () => ({}),
    },
    groupSessionId: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      searchText: "",
      members: [],
      loading: false,
      pageNo: 1,
      pageSize: 15,
      total: 0,
      hasMore: true,
      selectedMember: this.initialSelectedMember,
    };
  },
  computed: {
    ...mapState("live", ["allMemberStatus"]),
    displayMembers() {
      // 首先过滤掉自己
      const filteredMembers = this.members.filter((member) => {
        return member.user_id !== this.userInfo.user_id;
      });

      // 如果有搜索文本，显示过滤后的成员
      if (this.searchText) {
        return filteredMembers;
      }

      // 如果没有搜索文本，显示所有成员，但排除已选成员（如果有）
      if (this.selectedMember && this.selectedMember !== "all") {
        const selectedId =
          this.selectedMember.user_id || this.selectedMember.id;
        return filteredMembers.filter((member) => {
          const memberId = member.user_id;
          return memberId !== selectedId;
        });
      }

      return filteredMembers;
    },
  },
  watch: {
    searchText() {
      // 当搜索文本变化时，重置分页
      this.pageNo = 1;
      this.hasMore = true;
      this.members = [];
      this.fetchMembers();
    },
    showMemberSelect: {
      handler(val) {
        if (val) {
          // 每次打开列表都重新获取
          this.searchText = "";
          this.loading = false;
          this.total = 0;
          this.pageNo = 1;
          this.members = [];
          this.hasMore = true;
          this.fetchMembers();
        }
      },
      immediate: true,
    },
    initialSelectedMember: {
      handler(newVal) {
        if (newVal !== this.selectedMember) {
          this.selectedMember = newVal;
        }
      },
      immediate: true,
      deep: true,
    },
    allMemberStatus: {
      handler(newVal) {
        if (newVal) {
          this.members.forEach((item) => {
            const findItem = newVal.find(
              (fItem) => fItem.user_id === item.user_id
            );
            if (findItem) {
              item.nickname = findItem.name || findItem.nickname;
            }
          });
          const findItem = newVal.find(
            (fItem) => fItem.user_id === this.selectedMember.user_id
          );
          if (findItem) {
            this.selectedMember.nickname = findItem.name || findItem.nickname;
          }
          if (this.searchText) {
            this.pageNo = 1;
            this.hasMore = true;
            this.members = [];
            this.fetchMembers();
          }
        }
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    // 获取成员列表
    async fetchMembers() {
      if (!this.groupSessionId || this.loading || !this.hasMore) return;
      this.loading = true;
      try {
        const params = {
          room_id: this.groupSessionId,
          page_no: this.pageNo,
          page_size: this.pageSize,
          is_teacher:
            this.userInfo.isTeacher || this.userInfo.isAssistant ? 0 : 1, //仅获取老师
          is_online: 1,
        };

        // 如果有搜索文本，添加nickname参数
        if (this.searchText) {
          params.nickname = this.searchText;
        }

        const response = await getRoster(params);

        if (response && response.data) {
          const { list, total } = response.data;
          const processedList = list || [];

          // 如果是第一页，替换成员列表；否则追加
          if (this.pageNo === 1) {
            this.members = processedList;
          } else {
            this.members = [...this.members, ...processedList];
          }

          this.total = total || 0;
          this.hasMore = this.members.length < this.total;
          this.pageNo++;
          this.updatePopperPosition();
        }
        this.loading = false;
      } catch (error) {
        this.loading = false;
        console.error("获取成员列表失败:", error);
      }
    },

    handleScroll(e) {
      const { scrollTop, scrollHeight, clientHeight } = e.target;

      // 当滚动到距离底部20px时，加载更多
      if (
        scrollHeight - scrollTop - clientHeight < 20 &&
        !this.loading &&
        this.hasMore &&
        !this.searchText
      ) {
        this.loadMore();
      }
    },

    loadMore() {
      this.fetchMembers();
    },

    clearSearch() {
      this.searchText = "";
    },

    selectMember(member) {
      this.selectedMember = member;
      this.$emit("select", member);
    },

    // 更新popper位置
    updatePopperPosition() {
      this.$nextTick(() => {
        setTimeout(() => {
          if (this.$parent?.updatePopper) {
            this.$parent?.updatePopper();
          }
        }, 0);
      });
    },

    isSelected(member) {
      if (member === "all" && this.selectedMember === "all") {
        return true;
      }

      if (member === "all" || this.selectedMember === "all") {
        return false;
      }

      const memberId = typeof member === "object" ? member.user_id : member;
      const selectedId = this.selectedMember
        ? this.selectedMember.user_id
        : null;

      return memberId === selectedId;
    },

    getMemberName(member) {
      if (!member || member === "all") return "所有人";
      return member.nickname || "";
    },

    // 根据角色获取对应的标签文本
    getRoleLabel(role) {
      const roleMap = {
        1: "教师",
        2: "助教",
        3: "学生",
      };
      return roleMap[role] || "";
    },

    // 根据角色获取对应的样式类名
    getRoleClass(role) {
      const classMap = {
        1: "role-teacher",
        2: "role-assistant",
        3: "role-student",
      };
      return classMap[role] || "";
    },
  },
};
</script>

<style lang="scss" scoped>
.member-select {
  width: 150px;
  max-height: 228px;
  background: #18181b;
  border-radius: 6px 6px 6px 6px;
  border: 1px solid #27272a;
  padding: 8px;
  display: flex;
  flex-direction: column;

  .search-input {
    margin-bottom: 6px;
    flex-shrink: 0;

    :deep(.el-input__inner) {
      background: #27272a;
      box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.05);
      border-radius: 6px 6px 6px 6px;
      border: none;
      color: #f7f8fa;
      height: 28px;
      font-size: 12px;
      padding-left: 27px;
      padding-right: 25px;
      line-height: 28px;

      &::placeholder {
        color: #71717a;
      }
    }

    :deep(.el-input__prefix) {
      color: #a1a1aa;
      font-weight: bold;
      left: 2px;
      i {
        line-height: 28px;
      }
    }

    :deep(.el-input__suffix) {
      right: 5px;
      i {
        line-height: 28px;
        position: relative;
        &::before {
          position: absolute;
          content: "";
          display: block;
          width: 14px;
          height: 14px;
          right: 0;
          top: 50%;
          transform: translateY(-50%);
          background: url("../../../assets//images/live-chat/close-icon.png")
            no-repeat center center;
          background-size: 100% 100%;
        }
      }
    }

    :deep(.el-input__icon) {
      line-height: 32px;
    }

    .clear-icon {
      color: #a1a1aa;
      cursor: pointer;
      font-size: 16px;
      &:hover {
        color: #f7f8fa;
      }
    }
  }

  .member-list {
    display: flex;
    flex-direction: column;
    gap: 2px;
    overflow-y: auto;
    flex-grow: 1;
    padding-right: 4px;

    &::-webkit-scrollbar {
      width: 4px;
    }

    &::-webkit-scrollbar-thumb {
      background-color: #27272a;
      border-radius: 2px;
    }

    &::-webkit-scrollbar-track {
      background-color: transparent;
    }

    .member-item {
      display: flex;
      align-items: center;
      padding: 0 4px 0 6px;
      // background-color: transparent;
      border-radius: 6px;
      cursor: pointer;
      transition: all 0.2s ease;
      height: 28px;
      min-height: 28px;
      &.selected {
        .member-name {
          flex: 1;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          line-break: anywhere;
        }
      }

      &:hover {
        background-color: #3f3f46;
      }

      .member-icon {
        font-size: 16px;
        color: #f7f8fa;
      }

      .member-icon-wrapper,
      .member-avatar {
        width: 14px;
        height: 14px;
        object-fit: cover;
        border-radius: 50%;
        margin-right: 4px;
      }

      .member-info {
        flex: 1;
        display: flex;
        align-items: center;
        overflow: hidden;
        margin-right: 4px;

        .member-name {
          width: 0;
          flex: 1;
          display: flex;
          align-items: center;
          font-weight: 400;
          font-size: 12px;
          color: #f7f8fa;
          line-height: 20px;
          .member-name-text {
            flex: 1;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            line-break: anywhere;
          }
        }

        .role-tag {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0 2px;
          border-radius: 3px;
          font-weight: 500;
          font-size: 8px;
          color: #17c964;
          line-height: 1;
          height: 12px;
          background: #052814;
          margin-right: 4px;
          &.role-student {
            display: none;
          }
          &.role-assistant {
            color: #006fee;
            background: #001731;
          }
        }
      }

      .info-icon {
        font-size: 16px;
        color: #165dff;
        margin-left: auto;
      }

      .check-icon {
        font-size: 16px;
        color: #165dff;
        margin-left: auto;
        font-weight: bold;
      }

      &.all-members {
        color: #f7f8fa;
        background-color: #18181b;
        position: sticky;
        top: 0;
        z-index: 1;
        font-weight: 400;
        font-size: 12px;
        color: #f7f8fa;
        line-height: 20px;
        border-radius: 0;
      }
    }

    .no-result {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      font-weight: 400;
      font-size: 12px;
      color: #f7f8fa;
      line-height: 20px;
    }

    .loading-more {
      text-align: center;
      padding: 8px 0;
      color: #a1a1aa;
      font-size: 12px;
    }
  }
}
</style>
